import { store, myPlayer } from "../core/store";
import { socket } from "../core/socket";
import type { CCCCardView, Difficulty, GameConfig, WordOfferDTO } from "../../shared/types";
import { buildCCCNameHover, formatBonusPercent } from "./cccTooltip";

const DIFFICULTY_LABELS: Record<string, string> = {
  easy: "Easy",
  moderate: "Moderate",
  difficult: "Difficult",
};

function multiplierPercent(config: GameConfig | null, difficulty: Difficulty): number {
  const multiplier = config?.difficultyMultipliers[difficulty] ?? 1;
  return Math.round((multiplier - 1) * 100);
}

export function mountWordSelectionArtist(container: HTMLElement): () => void {
  const wrap = document.createElement("div");
  wrap.className = "word-selection-wrap";

  const heading = document.createElement("h2");
  heading.textContent = "Choose a word to draw";
  wrap.appendChild(heading);

  const scoringInfo = document.createElement("p");
  scoringInfo.className = "hint-text word-selection-scoring-info";
  wrap.appendChild(scoringInfo);

  const optionsRow = document.createElement("div");
  optionsRow.className = "word-options-row";
  wrap.appendChild(optionsRow);

  const redrawBlock = document.createElement("div");
  redrawBlock.className = "word-selection-redraw-block";
  wrap.appendChild(redrawBlock);

  const redrawBtn = document.createElement("button");
  redrawBtn.type = "button";
  redrawBtn.className = "secondary-button";
  redrawBtn.addEventListener("click", () => socket.emit("wordSelection:redraw"));
  redrawBlock.appendChild(redrawBtn);

  const redrawStatus = document.createElement("p");
  redrawStatus.className = "hint-text word-selection-redraw-status";
  redrawBlock.appendChild(redrawStatus);

  const activeCCCsBlock = document.createElement("div");
  activeCCCsBlock.className = "word-selection-active-cccs";
  wrap.appendChild(activeCCCsBlock);

  // The store notifies on every unrelated change too (chat, timer-adjacent snapshots).
  // Only rebuild the word-option buttons when the offer itself actually changes, so
  // a click in flight never lands on an element that's already been torn down. The
  // surrounding text blocks have no click handlers, so they're safe to refresh on
  // every render call.
  let lastOffer: WordOfferDTO | null = null;

  function render(offer: WordOfferDTO | null, config: GameConfig | null, activeCCCs: CCCCardView[]): void {
    const scoring = config?.scoring;
    scoringInfo.textContent =
      scoring !== undefined
        ? `(First correct guess rewards you with ${scoring.artistFirstGuess} points. ` +
          `Every additional correct guess rewards you with ${scoring.artistAdditionalGuess} points.)`
        : "";

    if (offer && scoring !== undefined) {
      const used = offer.redrawsUsed;
      const remaining = Math.max(0, scoring.maxRedraws - used);
      const freeRemaining = Math.max(0, scoring.freeRedraws - used);
      // The NEXT redraw is free as long as free ones remain - showing the flat
      // penalty here regardless would be misleading while a free one is still
      // available.
      const nextCost = freeRemaining > 0 ? 0 : scoring.redrawPenalty;
      const myScore = myPlayer(store.getState())?.score ?? 0;
      const canAfford = nextCost === 0 || myScore >= nextCost;
      redrawBtn.disabled = !offer.canRedraw || !canAfford;
      if (!canAfford) {
        redrawBtn.textContent = "Re-roll words (insufficient funds)";
      } else if (nextCost === 0) {
        redrawBtn.textContent = "Re-roll words (Free)";
      } else {
        redrawBtn.textContent = `Re-roll words (Pay ${nextCost} Points)`;
      }
      redrawStatus.textContent = `${remaining} re-roll(s) left this turn, ${freeRemaining} of those free`;
    } else {
      redrawBtn.disabled = true;
      redrawBtn.textContent = "Re-roll words";
      redrawStatus.textContent = "";
    }

    activeCCCsBlock.innerHTML = "";
    if (activeCCCs.length > 0) {
      const label = document.createElement("p");
      label.className = "word-selection-ccc-label";
      label.textContent = "Currently active Chaos Canvas Cards:";
      activeCCCsBlock.appendChild(label);
      const list = document.createElement("ul");
      list.className = "word-selection-ccc-list";
      for (const card of activeCCCs) {
        const item = document.createElement("li");
        item.appendChild(buildCCCNameHover(card));
        item.appendChild(document.createTextNode(`: +${formatBonusPercent(card)}% point multiplier`));
        list.appendChild(item);
      }
      activeCCCsBlock.appendChild(list);
    }

    if (offer === lastOffer) return;
    lastOffer = offer;

    optionsRow.innerHTML = "";
    if (!offer) return;
    for (const option of offer.options) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `word-option word-option--${option.difficulty}`;
      const diff = document.createElement("div");
      diff.className = "word-option-difficulty";
      diff.textContent = DIFFICULTY_LABELS[option.difficulty] ?? option.difficulty;
      const word = document.createElement("div");
      word.className = "word-option-word";
      word.textContent = option.word;
      const multiplier = document.createElement("div");
      multiplier.className = "word-option-multiplier";
      const pct = multiplierPercent(config, option.difficulty);
      multiplier.textContent = `(${pct >= 0 ? "+" : ""}${pct}% point multiplier)`;
      btn.append(diff, word, multiplier);
      btn.addEventListener("click", () => socket.emit("wordSelection:choose", option.index));
      optionsRow.appendChild(btn);
    }
  }

  render(store.getState().wordOffer, store.getState().config, store.getState().snapshot?.activeArtistCCCs ?? []);
  const unsub = store.subscribe((state) => render(state.wordOffer, state.config, state.snapshot?.activeArtistCCCs ?? []));
  container.appendChild(wrap);
  return () => unsub();
}

export function mountWordSelectionWaiting(container: HTMLElement): () => void {
  const wrap = document.createElement("div");
  wrap.className = "word-selection-waiting";

  // The store notifies on every unrelated change too (every chat message), and
  // rebuilding the cards on each one would restart their glow animation - only
  // rebuild when what's actually shown changes.
  let lastSignature: string | null = null;

  function render(): void {
    const state = store.getState();
    const artist = state.snapshot?.players.find((p) => p.id === state.snapshot?.currentArtistId);
    const cards = state.snapshot?.activeArtistCCCs ?? [];
    const signature = JSON.stringify([artist?.name ?? null, cards.map((c) => [c.familyId, c.level])]);
    if (signature === lastSignature) return;
    lastSignature = signature;

    wrap.innerHTML = "";
    const text = document.createElement("p");
    text.className = "word-selection-waiting-title";
    text.textContent = artist ? `${artist.name} is choosing a word to draw...` : "Choosing a word...";
    wrap.appendChild(text);

    // "The Gambler" shows as itself here, not as the cards it'll turn into -
    // those aren't decided until drawing starts (see getActiveArtistCCCViews).
    if (artist && cards.length > 0) {
      const label = document.createElement("p");
      label.className = "word-selection-waiting-ccc-label";
      label.textContent = `${artist.name} will be drawing with the following Chaos Canvas Cards active:`;
      wrap.appendChild(label);

      const row = document.createElement("div");
      row.className = "word-selection-waiting-cards";
      for (const card of cards) row.appendChild(buildStaticCCCCard(card));
      wrap.appendChild(row);
    }
  }

  render();
  const unsub = store.subscribe(render);
  container.appendChild(wrap);
  return () => unsub();
}

/** The same card look as the CCC selection screen, but a plain non-interactive
 * element - for showing what someone is holding rather than offering a choice. */
function buildStaticCCCCard(card: CCCCardView): HTMLElement {
  const el = document.createElement("div");
  el.className = "ccc-card ccc-card--static";

  const name = document.createElement("div");
  name.className = "ccc-card-name";
  name.textContent = card.name;
  const desc = document.createElement("div");
  desc.className = "ccc-card-desc";
  desc.textContent = card.description;
  const bonus = document.createElement("div");
  bonus.className = "ccc-card-bonus";
  bonus.textContent = `+${formatBonusPercent(card)}% drawing bonus points`;

  el.append(name, desc, bonus);
  return el;
}
